import type { LineItemElement } from "@/interfaces/line-item-elements-response";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { areValueDependenciesNotSatisfied, buildDisabledTooltip } from "./LineItemElementsGrid.utils";

interface LineItemElementsGridProps {
  elements: LineItemElement[];
  selections: string[];
  updateSelection: (index: number, value: string) => void;
}

export const LineItemElementsGrid = ({ elements, selections, updateSelection }: LineItemElementsGridProps) => {
  return (
    <TooltipProvider>
      <div className="grid w-fit auto-cols-max grid-flow-col gap-x-1">
        {elements.map((item, index) => {
          const isText = item.type === "text";
          const currentValue = isText ? item.value : selections[index];

          return (
            <div key={index} className="flex flex-col items-center">
              <div className="mb-2">
                {isText ? (
                  <p className="px-1 text-base font-normal whitespace-nowrap">{item.value.trim()}</p>
                ) : (
                  <p className="inline-block rounded-md bg-blue-50 px-1 text-base font-normal whitespace-nowrap text-blue-900">
                    {currentValue || ""}
                  </p>
                )}
              </div>
              <div className="!mr-[-4px] !ml-[-4px] h-[1px] w-full !bg-[#000]" />
              {!isText && (
                <div className="flex flex-col gap-1">
                  {item.acceptableValues?.map((option) => {
                    const deps = item.valueDependencies?.[option];
                    const isDisabled = areValueDependenciesNotSatisfied(deps, selections);
                    const isSelected = option === selections[index];
                    const tooltipText = isDisabled ? buildDisabledTooltip(deps!, selections) : undefined;

                    const optionEl = (
                      <div
                        key={option}
                        className={`inline-block rounded px-3 py-1 text-sm whitespace-nowrap ${
                          isDisabled
                            ? "cursor-not-allowed text-stone-400"
                            : isSelected
                              ? "cursor-pointer bg-blue-50 font-semibold text-blue-900"
                              : "cursor-pointer"
                        }`}
                        onClick={isDisabled ? undefined : () => updateSelection(index, option?.trim())}
                      >
                        {option}
                      </div>
                    );

                    if (isDisabled && tooltipText) {
                      return (
                        <Tooltip key={option}>
                          <TooltipTrigger asChild>{optionEl}</TooltipTrigger>
                          <TooltipContent>{tooltipText}</TooltipContent>
                        </Tooltip>
                      );
                    }

                    return optionEl;
                  })}

                  {item.allowEmptyValue && (
                    <div
                      className="inline-block cursor-pointer rounded px-3 py-1 text-sm whitespace-nowrap italic"
                      onClick={() => updateSelection(index, "")}
                    >
                      empty
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </TooltipProvider>
  );
};
